const bwipjs = require("bwip-js");
import loadBusinessLogo from "./loadBusinessLogo";

const clean = value => value === null || value === undefined ? "" : String(value).trim();
const compact = values => values.map(clean).filter(value => value && value.toLowerCase() !== "null");
const cityLine = (city, state, zip) => compact([compact([city, state]).join(", "), zip]).join(" ");

const makeBarcode = value => {
  const canvas = document.createElement("canvas");
  return new Promise((resolve, reject) => {
    bwipjs(canvas, { bcid: "code128", text: String(value), scale: 3, height: 4, includetext: false }, error => {
      if (error) reject(error);
      else resolve(canvas.toDataURL("image/png"));
    });
  });
};

export default {
  async create(invoice, business, settings) {
    const customer = invoice.Customer || {};
    const logo = await loadBusinessLogo(business);
    const barcode = settings.showInvoiceBarcode ? await makeBarcode(invoice.localId) : null;
    const businessLines = compact([
      business.name,
      business.address || business.address1,
      cityLine(business.city, business.state, business.zip),
      business.phoneNumber,
      business.email,
      business.website,
    ]);
    const customerLines = compact([
      customer.name || "Walk-In Customer",
      customer.address,
      cityLine(customer.city, customer.state, customer.zip),
    ]);
    const header = [];
    const widths = [];
    if (settings.showLineNumber) {
      header.push("LINE");
      widths.push(36);
    }
    header.push("ITEM DESCRIPTION");
    widths.push("*");
    if (settings.serializeEnabled) {
      header.push("IMEI / SERIAL NUMBER");
      widths.push(135);
    }
    header.push("QTY");
    widths.push(50);
    const rows = (invoice.InvoiceDetails || []).map((item, index) => {
      const row = [];
      if (settings.showLineNumber) row.push({ text: String(index + 1), alignment: "center", color: "#64748B" });
      row.push({ text: clean(item.name), color: "#0F172A" });
      if (settings.serializeEnabled) row.push({ text: clean(item.imeiNumber), color: "#334155", fontSize: 8 });
      row.push({ text: String(Number(item.qty || 0)), alignment: "center", bold: true });
      return row;
    });
    const totalQty = (invoice.InvoiceDetails || []).reduce((sum, item) => sum + Number(item.qty || 0), 0);

    return {
      info: { title: `Packing Slip #${invoice.localId}` },
      pageSize: "LETTER",
      pageOrientation: "portrait",
      pageMargins: [36, 38, 36, 38],
      defaultStyle: { fontSize: 9, color: "#0F172A" },
      footer: (page, pages) => ({
        columns: [{ text: "Powered by smplsale.com", color: "#94A3B8", fontSize: 7 }, { text: `${page} of ${pages}`, alignment: "right", color: "#94A3B8", fontSize: 7 }],
        margin: [36, 0, 36, 14],
      }),
      content: [
        ...(logo ? [{ image: logo, fit: [70, 45], margin: [0, 0, 0, 5] }] : []),
        {
          columns: [
            {
              width: "*",
              columns: [{ stack: businessLines.map((line, index) => ({ text: line, bold: index === 0, fontSize: index === 0 ? 14 : 9, lineHeight: 1.15 })) }],
            },
            {
              width: 190,
              stack: [
                { text: "PACKING SLIP", alignment: "right", bold: true, fontSize: 23, color: "#2563EB" },
                { text: `#${invoice.localId}`, alignment: "right", bold: true, fontSize: 12, margin: [0, 4, 0, barcode ? 10 : 4] },
                ...(barcode ? [{ image: barcode, width: 130, alignment: "right", margin: [0, 0, 0, 9] }] : []),
                { text: `Invoice #  ${invoice.localId}`, alignment: "right", margin: [0, 0, 0, 3] },
                { text: `Date  ${settings.moment(invoice.date || invoice.createdAt).format("MM/DD/YYYY")}`, alignment: "right", margin: [0, 0, 0, 3] },
                { text: `Sales Rep  ${clean(invoice.User && invoice.User.name)}`, alignment: "right" },
              ],
            },
          ],
          margin: [0, 0, 0, 24],
        },
        {
          stack: [
            { text: "SHIP TO", bold: true, fontSize: 8, color: "#64748B", margin: [0, 0, 0, 5] },
            ...customerLines.map((line, index) => ({ text: line, bold: index === 0, fontSize: index === 0 ? 11 : 9 })),
          ],
          margin: [0, 0, 0, 22],
        },
        {
          table: {
            headerRows: 1,
            widths,
            body: [header.map((text, index) => ({ text, bold: true, color: "#FFFFFF", alignment: index === 0 || index === header.length - 1 ? "center" : "left" })), ...rows],
          },
          layout: {
            fillColor: row => row === 0 ? "#2563EB" : row % 2 === 0 ? "#F8FAFC" : "#FFFFFF",
            hLineColor: () => "#CBD5E1",
            hLineWidth: index => index === 0 ? 0 : 0.5,
            vLineWidth: () => 0,
            paddingTop: () => 7,
            paddingBottom: () => 7,
            paddingLeft: () => 7,
            paddingRight: () => 7,
          },
        },
        {
          columns: [
            { text: `${totalQty} ITEM(S)`, bold: true, color: "#475569", margin: [0, 12, 0, 0] },
            { text: "Received by: ______________________________", alignment: "right", color: "#64748B", margin: [0, 12, 0, 0] },
          ],
        },
      ],
    };
  },
};
